import type { CoachPlanBody, CoachPlanDocType } from '../validators';
import type { Assert, AssertEqual } from './type-assertions';
import type { ExtractDocumentTypeFromTypedRxJsonSchema, RxJsonSchema } from 'rxdb';
import { defineRxdbCollectionModel } from './migration';

// ============================================================================
// RxDB collection schema for the coach's proposed plan.
//
// Document shape mirrors the D1 `coach_plans` row. `body` is an opaque object
// in the RxDB schema and refined to `CoachPlanBody` (mirrors diaryDays). Uses
// `updatedAtUtc` as the checkpoint field.
// ============================================================================

export const COACH_PLANS_COLLECTION = 'coachPlans' as const;

export const coachPlansSchemaLiteral = {
  title: 'coach plans schema',
  version: 0,
  primaryKey: 'id',
  type: 'object',
  properties: {
    id: { type: 'string', maxLength: 100 },
    userId: { type: 'string', maxLength: 100 },
    status: {
      type: 'string',
      enum: ['draft', 'pending_approval', 'approved', 'revoked'],
    },
    body: { type: 'object' },
    sentAtUtc: { type: ['string', 'null'] },
    createdAtUtc: { type: 'string', maxLength: 30 },
    updatedAtUtc: { type: 'string', maxLength: 30 },
    deletedAt: { type: ['string', 'null'] },
  },
  required: ['id', 'userId', 'status', 'body', 'sentAtUtc', 'createdAtUtc', 'updatedAtUtc', 'deletedAt'],
  indexes: [['userId', 'updatedAtUtc']],
} as const;

export const COACH_PLANS_SCHEMA_VERSION = coachPlansSchemaLiteral.version;

export interface CoachPlansCheckpoint {
  updatedAtUtc: string;
  id: string;
}

type _CoachPlanSchemaDocType = Omit<
  ExtractDocumentTypeFromTypedRxJsonSchema<typeof coachPlansSchemaLiteral>,
  'body'
> & { body: CoachPlanBody };
type _CoachPlanDocTypeInSync = Assert<AssertEqual<_CoachPlanSchemaDocType, CoachPlanDocType>>;

export const coachPlansSchema: RxJsonSchema<CoachPlanDocType> = coachPlansSchemaLiteral;

export const coachPlansModel = defineRxdbCollectionModel<typeof COACH_PLANS_COLLECTION, CoachPlanDocType>({
  collection: COACH_PLANS_COLLECTION,
  schema: coachPlansSchema,
});
